import { useEffect, useState } from "react";
import { Button, Form, Spinner, Badge } from "react-bootstrap";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { JsonView, allExpanded, defaultStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { LaunchNetworkService } from "@services/engagements/launch/launchNetworkService";
import {
  NETWORK_LINK_STATUSES,
  audienceSizeSummary,
  type NetworkLinkState,
} from "@models/launchNetworkLinks.model";

interface NetworkLinkInspectorProps {
  launchId: number;
  network: string;
  link: NetworkLinkState | null;
  onSaved: (updated: NetworkLinkState) => void;
}

const TYPE_LABELS: Record<NetworkLinkState["object_type"], string> = {
  campaign: "Campaign",
  ad_group: "Ad Group",
  audience: "Audience",
  ad: "Ad",
};

export const NetworkLinkInspector = ({ launchId, network, link, onSaved }: NetworkLinkInspectorProps) => {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState("");
  const [networkIds, setNetworkIds] = useState("");
  const [dirty, setDirty] = useState(false);
  const [dataDraft, setDataDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset edit state whenever the selected link changes.
  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [link?.object_type, link?.object_id]);

  if (!link) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <p className="text-muted">Select a link object to inspect.</p>
      </div>
    );
  }

  const startEditing = () => {
    setStatus(link.status);
    setNetworkIds(link.network_ids.join(", "));
    setDirty(link.dirty);
    setDataDraft(JSON.stringify(link.data ?? {}, null, 2));
    setError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    let parsedData: Record<string, any>;
    try {
      parsedData = JSON.parse(dataDraft);
    } catch {
      setError("Invalid JSON in data — fix the syntax before saving.");
      return;
    }

    const ids = networkIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    setSaving(true);
    setError(null);
    try {
      const updated = await new LaunchNetworkService().patchLink(
        launchId,
        network,
        link.object_type,
        link.object_id,
        { status, network_ids: ids, dirty, data: parsedData },
      );
      onSaved(updated);
      setEditing(false);
    } catch (err) {
      console.error("Failed to patch link:", err);
      setError(err instanceof Error ? err.message : "Failed to save link.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 h-100 d-flex flex-column" style={{ overflowY: "auto" }}>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h6 className="mb-1">
            {link.object_name || TYPE_LABELS[link.object_type]}{" "}
            <span className="text-muted">{TYPE_LABELS[link.object_type]} #{link.object_id}</span>
          </h6>
          <Badge bg={link.dirty ? "warning" : "secondary"}>{link.status}</Badge>
          {link.dirty && <Badge bg="warning" className="ms-2">dirty</Badge>}
        </div>

        {editing ? (
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" /> : "Save"}
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline-primary" onClick={startEditing} className="d-flex align-items-center gap-1">
            <i className="bi bi-pencil"></i>
            Edit
          </Button>
        )}
      </div>

      {error && <div className="alert alert-danger py-2 px-3">{error}</div>}

      {editing ? (
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold mb-1">Status</Form.Label>
            <Form.Select value={status} onChange={(e) => setStatus(e.currentTarget.value)}>
              {NETWORK_LINK_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold mb-1">Network IDs</Form.Label>
            <Form.Control
              value={networkIds}
              onChange={(e) => setNetworkIds(e.currentTarget.value)}
              placeholder="comma-separated, e.g. 626758285244, 626758285245"
            />
            <Form.Text className="text-muted">Comma-separated. Replaced wholesale on save.</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="dirty"
              checked={dirty}
              onChange={(e) => setDirty(e.currentTarget.checked)}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="fw-semibold mb-1">Data (JSON)</Form.Label>
            <div className="border rounded">
              <CodeMirror
                value={dataDraft}
                onChange={setDataDraft}
                extensions={[json()]}
                basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true, closeBrackets: true }}
                minHeight="160px"
              />
            </div>
          </Form.Group>
        </Form>
      ) : (
        <dl className="row mb-0">
          <dt className="col-sm-4">Current network ID</dt>
          <dd className="col-sm-8"><code>{link.current_network_id ?? "—"}</code></dd>

          <dt className="col-sm-4">Network IDs</dt>
          <dd className="col-sm-8">
            {link.network_ids.length ? link.network_ids.map((id) => <code key={id} className="me-2">{id}</code>) : "—"}
          </dd>

          {link.object_type === "audience" && (
            <>
              {/* MPN-1971: the gap Kayla found in Meta's UI, visible without leaving ours. */}
              <dt className="col-sm-4">Members uploaded</dt>
              <dd className="col-sm-8">{link.uploaded_members?.toLocaleString() ?? "—"}</dd>

              <dt className="col-sm-4">Network reports</dt>
              <dd className="col-sm-8">
                {link.matched_count == null ? (
                  <span className="text-muted">No figure yet</span>
                ) : (
                  <>
                    {audienceSizeSummary(link) ?? link.matched_count.toLocaleString()}
                    {link.matched_at && (
                      <span className="text-muted ms-2">as of {new Date(link.matched_at).toLocaleString()}</span>
                    )}
                  </>
                )}
              </dd>
            </>
          )}

          <dt className="col-sm-4">Data</dt>
          <dd className="col-sm-8">
            <div className="border rounded p-2 bg-white">
              <JsonView data={link.data ?? {}} style={defaultStyles} shouldExpandNode={allExpanded} />
            </div>
          </dd>
        </dl>
      )}
    </div>
  );
};
